import { Toast } from '@components/Toast';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { StepNotice as StepNoticeEntity } from '@partful/entities/instructions/stepNotice';
import { useCreateStepNotice } from '@partful/features/instructions/steps/lib/useCreateStepNotice';
import { useDeleteStepNotice } from '@partful/features/instructions/steps/lib/useDeleteStepNotice';
import { useListStepNotices } from '@partful/features/instructions/steps/lib/useListStepNotices';
import { useUpdateStepNotice } from '@partful/features/instructions/steps/lib/useUpdateStepNotice';
import { Button } from '@partful/shared/ui/Button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogPanel } from '@partful/shared/ui/Dialog/Dialog';
import { Typography } from '@partful/shared/ui/Typography';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StepNotice } from './StepNotice/StepNotice';
import { StepNoticeForm } from './StepNoticeForm/StepNoticeForm';
import styles from './StepNotices.module.scss';

export interface StepNoticesProps {
  instructionId: string;
  versionId: string;
  segmentId: string;
  stepId: string;
  languageCode: string;
}

export function StepNotices({ instructionId, versionId, segmentId, stepId, languageCode }: Readonly<StepNoticesProps>) {
  const { t: i18n } = useTranslation();
  const { data: list } = useListStepNotices(instructionId, versionId, segmentId, stepId, languageCode);
  const {
    mutate: create,
    isLoading: isCreating,
    isSuccess: isCreatedSuccessfully,
    error: isCreatedError,
  } = useCreateStepNotice();
  const {
    mutate: update,
    isLoading: isUpdating,
    isSuccess: isUpdatedSuccessfully,
    error: isUpdateError,
  } = useUpdateStepNotice();

  // Defines where and whether to show the step notice form.
  const [formIndex, setFormIndex] = useState<number>();

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [stepNoticeToDelete, setStepNoticeToDelete] = useState<string>('');

  const {
    mutateAsync: deleteStepNotice,
    isLoading: isDeleting,
    isError: isErrorDeleting,
    isSuccess: isSuccessDeleting,
  } = useDeleteStepNotice();

  const handleDelete = async (stepNoticeId: string) => {
    try {
      await deleteStepNotice({
        versionId,
        instructionId,
        segmentId,
        stepId,
        stepNoticeId,
      });
      setConfirmDelete(false);
    } catch (e) {
      // Don't need to handle this here
    }
  };

  const onSubmit = useCallback(
    (values: StepNoticeEntity) => {
      if (values.id == null) {
        create({
          instructionId,
          versionId,
          segmentId,
          stepId,
          args: {
            order: list?.length ?? /* istanbul ignore next - very defensive */ 0,
            type: values.type,
            content: { languageCode, title: values.title, description: values.description },
          },
        });
      } else {
        const existing = list?.find((item) => item.id === values.id);
        update({
          instructionId,
          versionId,
          segmentId,
          stepId,
          stepNoticeId: values.id,
          languageCode,
          args: {
            __expectedVersion: existing?.__version ?? /* istanbul ignore next - very defensive */ 1,
            type: values.type,
            content: {
              __expectedVersion: existing?.content?.__version ?? /* istanbul ignore next - very defensive */ 1,
              title: values.title,
              description: values.description,
            },
          },
        });
      }
    },
    [create, instructionId, languageCode, list, segmentId, stepId, update, versionId]
  );

  useEffect(() => {
    if (isCreatedSuccessfully || isUpdatedSuccessfully) {
      setFormIndex(undefined);
    }
  }, [isCreatedSuccessfully, isUpdatedSuccessfully]);

  return (
    <div className={styles.notices}>
      <Typography fontWeight={600} as="label">
        {i18n('instructions:stepBuilder.notices')}
      </Typography>
      {list?.map((notice, i) =>
        formIndex === i ? (
          <StepNoticeForm
            key={notice.id}
            disabled={isUpdating}
            initialValues={notice}
            onCancelClick={() => setFormIndex(undefined)}
            onSubmit={onSubmit}
          />
        ) : (
          <StepNotice
            key={notice.id}
            notice={notice}
            index={i}
            onEditClick={() => setFormIndex(i)}
            onDeleteClick={() => {
              setConfirmDelete(true);
              setStepNoticeToDelete(notice.id);
            }}
          />
        )
      )}
      {formIndex === -1 ? (
        <StepNoticeForm disabled={isCreating} onCancelClick={() => setFormIndex(undefined)} onSubmit={onSubmit} />
      ) : null}
      {formIndex == null ? (
        <Button
          name="instructions_stepeditor_add_notice_click"
          variant="secondary"
          size="small"
          onClick={() => setFormIndex(-1)}
          data-testid="add-notice-btn"
        >
          <FontAwesomeIcon icon={faPlus} />
          {i18n('instructions:stepBuilder.addNotice')}
        </Button>
      ) : null}
      <Dialog
        open={confirmDelete}
        modal
        onClose={/* istanbul ignore next - not testing native <dialog> behaviour */ () => setConfirmDelete(false)}
      >
        <DialogPanel>
          <DialogHeader>
            <Typography as="p" appearance="heading5" overflow="hidden" textOverflow="ellipsis">
              {i18n('instructions:step.notice.deleteConfirm.title')}
            </Typography>
          </DialogHeader>
          <DialogContent>{i18n('instructions:step.notice.deleteConfirm.message')}</DialogContent>
          <DialogFooter>
            <Button
              name="instructions_stepeditor_delete_notice_confirm_click"
              data-testid="confirm-delete-btn"
              variant="destructive"
              disabled={isDeleting}
              onClick={() => handleDelete(stepNoticeToDelete)}
            >
              {i18n('instructions:common.delete')}
            </Button>
            <Button
              name="instructions_stepeditor_delete_notice_cancel_confirm_click"
              variant="tertiary"
              disabled={isDeleting}
              onClick={/* istanbul ignore next */ () => setConfirmDelete(false)}
            >
              {i18n('instructions:common.cancel')}
            </Button>
          </DialogFooter>
        </DialogPanel>
      </Dialog>
      <Toast
        dependency={isCreatedError}
        severity="error"
        title={i18n('instructions:step.notice.createError.title')}
        message={i18n('instructions:step.notice.createError.message')}
        testId="create-step-notice-error-message"
      />
      <Toast
        dependency={isCreatedSuccessfully}
        severity="success"
        title={i18n('instructions:step.notice.createSuccess.title')}
        testId="create-step-notice-success-message"
      />
      <Toast
        dependency={isUpdateError}
        severity="error"
        title={i18n('instructions:step.notice.updateError.title')}
        message={i18n('instructions:step.notice.updateError.message')}
        testId="update-step-notice-error-message"
      />
      <Toast
        dependency={isUpdatedSuccessfully}
        severity="success"
        title={i18n('instructions:step.notice.updateSuccess.title')}
        testId="update-step-notice-success-message"
      />
      <Toast
        dependency={isErrorDeleting}
        severity="error"
        title={i18n('instructions:step.notice.deleteError.title')}
        message={i18n('instructions:step.notice.deleteError.message')}
        testId="delete-step-notice-error-message"
      />
      <Toast
        dependency={isSuccessDeleting}
        severity="success"
        title={i18n('instructions:step.notice.deleteSuccess.title')}
        testId="delete-step-notice-success-message"
      />
    </div>
  );
}
